import { useQuery } from '@tanstack/react-query'
import type { CalendarLeave, LeaveDetailPayload } from '@lms/shared-types'
import { getLeaveDetail } from '../../api/leaves'
import { listPublicHolidays } from '../../api/public-holidays'
import { Badge } from '../primitives/Badge'
import { Skeleton } from '../primitives/Skeleton'

/**
 * Per-day breakdown of a leave, showing each WORKING day of the range —
 * skipping weekends and public holidays — marked as Full or ½ AM / ½ PM.
 *
 * Three entry points share one presentational core (`LeaveDayBreakdownList`):
 *  - `LeaveDayBreakdown({ leaveId })`     — fetches the detail itself; used by
 *    the calendar popover / hover tooltip and the Upcoming-leave row, where the
 *    caller only holds a leave id. The `['leaves', id, 'detail']` query key is
 *    shared, so hovering then clicking a leave fetches it once.
 *  - `LeaveDayBreakdownFromDetail({ detail })` — renders from an already-loaded
 *    `LeaveDetailPayload` (the leave-details page keeps it in local state), so
 *    that surface adds no extra detail fetch.
 *  - `LeaveDayBreakdownList({ rows })`    — pure presentational, no data access.
 *
 * `tone` adapts the styling: `'surface'` (default) for light cards, `'tooltip'`
 * for the dark hover tooltip (compact, light-on-dark text, no chips).
 *
 * Only meaningful for half-day-capable types (annual / medical / unpaid /
 * replacement), which count working days — so weekend/holiday exclusion is
 * correct. Callers gate on `LEAVE_TYPE_HALF_DAY_ALLOWED`; elapsed-day types
 * (compassionate, maternity, …) never reach this component.
 */
export type BreakdownTone = 'surface' | 'tooltip'

interface DayRow {
  iso: string
  weekday: string
  holiday: string | null
  /** Explicit AM/PM slot when known; null when the day is a half via the
   *  legacy half_day/half_start/half_end fallback (slot unknown) or is full. */
  slot: 'AM' | 'PM' | null
  half: boolean
}

export type { DayRow }

function localDate(iso: string): Date {
  return new Date(`${iso}T00:00:00`)
}

function toISO(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd}`
}

function dmy(iso: string): string {
  return localDate(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

function weekdayShort(iso: string): string {
  return localDate(iso).toLocaleDateString('en-GB', { weekday: 'short' })
}

/** Human label for a half-day slot. AM → morning, PM → afternoon. When the
 *  slot is unknown (legacy half_day flag with no per-date row), we can't say
 *  which half, so fall back to a neutral label. */
export function slotLabel(slot: 'AM' | 'PM' | null): string {
  if (slot === 'AM') return '1st half'
  if (slot === 'PM') return '2nd half'
  return 'Half day'
}

/**
 * Enumerate the leave's working days (Mon–Fri), tagging public holidays and
 * half-day slots. Weekends are skipped entirely; holidays are listed but
 * marked as not-counted so the gap in the sequence is explained.
 */
export function buildDayRows(
  startISO: string,
  endISO: string,
  holidayNames: ReadonlyMap<string, string>,
  halfSlots: ReadonlyMap<string, 'AM' | 'PM'>,
  // Legacy half-day flags for leaves without per-date rows: a single-day
  // `half_day`, or `half_start`/`half_end` on a multi-day range. Mirrors the
  // fallback in the API's `_compute_leave_days`.
  opts: { halfDay?: boolean; halfStart?: boolean; halfEnd?: boolean } = {},
): DayRow[] {
  const rows: DayRow[] = []
  let cursor = localDate(startISO)
  const end = localDate(endISO)
  // Guard against a malformed range producing an unbounded loop.
  let guard = 0
  while (cursor <= end && guard < 366) {
    guard += 1
    const dow = cursor.getDay()
    const iso = toISO(cursor)
    if (dow !== 0 && dow !== 6) {
      const holiday = holidayNames.get(iso) ?? null
      const slot = halfSlots.get(iso) ?? null
      // Holidays aren't counted, so never a half. Otherwise a day is a half
      // either via an explicit slot or via the derived legacy flags.
      const fallbackHalf =
        holiday === null &&
        slot === null &&
        ((startISO === endISO && Boolean(opts.halfDay)) ||
          (iso === startISO && Boolean(opts.halfStart)) ||
          (iso === endISO && Boolean(opts.halfEnd)))
      rows.push({
        iso,
        weekday: weekdayShort(iso),
        holiday,
        slot,
        half: slot !== null || fallbackHalf,
      })
    }
    cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1)
  }
  return rows
}

/** Build the working-day rows for a full leave-detail payload, given the
 *  public-holiday name map. Kept as a standalone so both fetching wrappers
 *  and tests can share the detail → rows mapping. */
export function buildRowsFromDetail(
  detail: LeaveDetailPayload,
  holidayNames: ReadonlyMap<string, string>,
): DayRow[] {
  // `half_days` is guaranteed by the schema (`.default([])`), but guard anyway:
  // a stale/older payload — or a Vite pre-bundle cached before the field was
  // added — can surface it as undefined, and this reader must not hard-crash.
  const halfSlots = new Map<string, 'AM' | 'PM'>(
    (detail.half_days ?? []).map((h) => [h.date, h.slot]),
  )
  return buildDayRows(detail.start_date, detail.end_date, holidayNames, halfSlots, {
    halfDay: detail.half_day,
    halfStart: detail.half_start,
    halfEnd: detail.half_end,
  })
}

/** Shared public-holiday name lookup, cached for the session. */
function useHolidayNames(): { holidayNames: Map<string, string>; isLoading: boolean } {
  const holidaysQuery = useQuery({
    queryKey: ['public-holidays'],
    queryFn: listPublicHolidays,
    staleTime: 60 * 60 * 1000, // holidays rarely change within a session
  })
  const holidayNames = new Map<string, string>(
    (holidaysQuery.data ?? []).map((h) => [h.date, h.name]),
  )
  return { holidayNames, isLoading: holidaysQuery.isLoading }
}

interface SkeletonProps {
  tone: BreakdownTone
}

function BreakdownSkeleton({ tone }: SkeletonProps): JSX.Element {
  if (tone === 'tooltip') {
    return (
      <div style={{ fontSize: 11, opacity: 0.7, paddingTop: 4 }}>Loading breakdown…</div>
    )
  }
  return (
    <div style={{ padding: '4px 8px 10px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Skeleton height={16} />
      <Skeleton height={16} />
      <Skeleton height={16} />
    </div>
  )
}

/**
 * Presentational per-day list. `tone` switches between the light card treatment
 * (Badge chips) and the compact dark-tooltip treatment (plain light text).
 */
export function LeaveDayBreakdownList({
  rows,
  tone = 'surface',
}: {
  rows: ReadonlyArray<DayRow>
  tone?: BreakdownTone
}): JSX.Element | null {
  if (rows.length === 0) return null

  if (tone === 'tooltip') {
    return (
      <div
        data-testid="leave-day-breakdown"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          marginTop: 4,
          paddingTop: 4,
          borderTop: '1px solid rgba(255, 255, 255, 0.15)',
        }}
      >
        {rows.map((row) => (
          <div
            key={row.iso}
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 6,
              fontSize: 11,
              opacity: row.holiday ? 0.6 : 0.9,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            <span style={{ width: 30, flexShrink: 0, opacity: 0.7 }}>{row.weekday}</span>
            <span style={{ flex: 1, minWidth: 0 }}>{dmy(row.iso)}</span>
            <span style={{ fontWeight: 600 }}>
              {row.holiday ? 'Holiday' : row.half ? slotLabel(row.slot) : 'Full'}
            </span>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div data-testid="leave-day-breakdown" style={{ padding: '2px 8px 10px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {rows.map((row) => (
          <div
            key={row.iso}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '6px 4px',
              opacity: row.holiday ? 0.6 : 1,
            }}
          >
            <span
              style={{
                fontSize: 11.5,
                color: 'var(--text-3)',
                fontFamily: 'var(--font-mono)',
                width: 34,
                flexShrink: 0,
              }}
            >
              {row.weekday}
            </span>
            <span
              style={{
                fontSize: 12,
                color: 'var(--text-2)',
                fontVariantNumeric: 'tabular-nums',
                flex: 1,
                minWidth: 0,
              }}
            >
              {dmy(row.iso)}
              {row.holiday ? (
                <span style={{ color: 'var(--text-3)' }}> · {row.holiday}</span>
              ) : null}
            </span>
            {row.holiday ? (
              <span style={{ fontSize: 11, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
                Public holiday
              </span>
            ) : row.half ? (
              <Badge variant="default">{slotLabel(row.slot)}</Badge>
            ) : (
              <Badge variant="default">Full</Badge>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

/** Render a day-by-day breakdown from an already-loaded leave detail. Only
 *  the public-holiday list is fetched (cached). No extra detail request. */
export function LeaveDayBreakdownFromDetail({
  detail,
  tone = 'surface',
}: {
  detail: LeaveDetailPayload
  tone?: BreakdownTone
}): JSX.Element | null {
  const { holidayNames, isLoading } = useHolidayNames()
  if (isLoading) return <BreakdownSkeleton tone={tone} />
  const rows = buildRowsFromDetail(detail, holidayNames)
  return <LeaveDayBreakdownList rows={rows} tone={tone} />
}

/** Whether the day popover offers a per-day breakdown for this leave.
 *
 *  Every leave qualifies, on purpose. The breakdown is always rendered as a
 *  *working-day* list (weekends/holidays skipped, AM/PM where the leave has
 *  half-day slots). Rendering EVERY leave the same way is deliberate: if
 *  continuous/elapsed types (hospitalization, maternity, …) rendered their
 *  weekends while working-day types didn't, a peer could infer the leave's
 *  category and partly defeat the type masking. A uniform working-day list
 *  leaks nothing, and keeps the popover consistent — a single-day leave lists
 *  its one day, a long leave lists each working day. `_leave` is accepted so
 *  the signature can tighten later without touching call sites. */
export function leaveSupportsBreakdown(
  _leave: Pick<CalendarLeave, 'halfDayAllowed'>,
): boolean {
  return true
}

/** Whether to PROACTIVELY surface the breakdown (e.g. in the hover tooltip):
 *  the type supports it AND the leave spans more than a single full day, so the
 *  per-day list adds information beyond the date range already shown. The day
 *  popover uses `leaveSupportsBreakdown` instead — an explicit click wants the
 *  days even for a one-day leave. */
export function hasDayBreakdown(
  leave: Pick<CalendarLeave, 'halfDayAllowed' | 'startDate' | 'endDate' | 'halfDay'>,
): boolean {
  return (
    leaveSupportsBreakdown(leave) &&
    (leave.startDate !== leave.endDate || leave.halfDay)
  )
}

/** Render a day-by-day breakdown straight from a calendar leave — no detail
 *  fetch. The calendar payload carries the day structure (start/end + half
 *  slots) but never the type/reason, so this is safe to show for ANY leave the
 *  viewer can see on the calendar, including a peer's whose type is masked.
 *  Only the (public) holiday list is fetched, and that's cached. */
export function LeaveDayBreakdownFromCalendar({
  leave,
  tone = 'surface',
}: {
  leave: Pick<
    CalendarLeave,
    'startDate' | 'endDate' | 'halfDay' | 'halfStart' | 'halfEnd' | 'halfDays'
  >
  tone?: BreakdownTone
}): JSX.Element | null {
  const { rows, isLoading } = useCalendarLeaveDayRows(leave)
  if (isLoading) return <BreakdownSkeleton tone={tone} />
  return <LeaveDayBreakdownList rows={rows} tone={tone} />
}

type CalendarDayStructure = Pick<
  CalendarLeave,
  'startDate' | 'endDate' | 'halfDay' | 'halfStart' | 'halfEnd' | 'halfDays'
>

/** Working-day rows for a calendar leave (weekends skipped, holidays flagged).
 *  While holidays load, rows are built without them — weekends are already
 *  right, and the count settles once the cached holiday list arrives. */
export function useCalendarLeaveDayRows(leave: CalendarDayStructure): {
  rows: DayRow[]
  isLoading: boolean
} {
  const { holidayNames, isLoading } = useHolidayNames()
  const halfSlots = new Map<string, 'AM' | 'PM'>(
    (leave.halfDays ?? []).map((h) => [h.date, h.slot]),
  )
  const rows = buildDayRows(leave.startDate, leave.endDate, holidayNames, halfSlots, {
    halfDay: leave.halfDay,
    halfStart: leave.halfStart ?? false,
    halfEnd: leave.halfEnd ?? false,
  })
  return { rows, isLoading }
}

/** Days the breakdown rows add up to: holidays count 0, halves 0.5. */
export function countDayRows(rows: readonly DayRow[]): number {
  return rows.reduce((sum, row) => sum + (row.holiday ? 0 : row.half ? 0.5 : 1), 0)
}

export interface LeaveDayBreakdownProps {
  leaveId: string
  tone?: BreakdownTone
}

/** Fetch a leave's detail (cached by id) plus holidays, then render the
 *  per-day breakdown. Used where the caller only holds a leave id. */
export function LeaveDayBreakdown({
  leaveId,
  tone = 'surface',
}: LeaveDayBreakdownProps): JSX.Element | null {
  const detailQuery = useQuery({
    queryKey: ['leaves', leaveId, 'detail'],
    queryFn: () => getLeaveDetail(leaveId),
  })
  const { holidayNames, isLoading: holidaysLoading } = useHolidayNames()

  if (detailQuery.isLoading || holidaysLoading) {
    return <BreakdownSkeleton tone={tone} />
  }

  if (detailQuery.isError || detailQuery.data === undefined) {
    // On the dark tooltip, degrade silently to the base tooltip content — a
    // red error line would be jarring and the day breakdown is supplementary.
    if (tone === 'tooltip') return null
    return (
      <div style={{ padding: '4px 8px 10px', fontSize: 12, color: 'var(--danger-fg)' }}>
        Couldn't load the day breakdown. Please try again.
      </div>
    )
  }

  const rows = buildRowsFromDetail(detailQuery.data, holidayNames)
  return <LeaveDayBreakdownList rows={rows} tone={tone} />
}
